import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { MessageCircle, Mail, Phone, Trash2, CheckCircle2, Circle, Clock, Archive, Loader2, RefreshCw, Reply, BellRing, Save, Pencil, Plus, Settings2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUS_META = {
  new: { label: "Nuova", cls: "bg-sky-100 text-sky-800 border-sky-300", icon: Circle },
  in_progress: { label: "In lavorazione", cls: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock },
  closed: { label: "Chiusa", cls: "bg-slate-100 text-slate-700 border-slate-300", icon: Archive },
};

// 🆕 Feb 2026 — Template risposte pronte editabili dal SuperAdmin.
// Il placeholder {name} nel corpo o nell'oggetto viene sostituito col nome del contatto.
// Se il DB non ne contiene ancora, mostriamo questi default come suggerimento (read-only).
const DEFAULT_REPLY_TEMPLATES = [
  {
    id: "__default_callback",
    label: "📞 Ti chiamo io",
    subject: "Ti ricontatto io a breve",
    body:
      "Ciao {name},\n\n" +
      "Grazie per averci scritto. Ti ricontatto io personalmente entro le prossime ore ai recapiti che ci hai lasciato per approfondire la tua richiesta.\n\n" +
      "A prestissimo,\nIl team GestAgePro",
  },
  {
    id: "__default_pricelist",
    label: "💼 Ecco il listino",
    subject: "Il nostro listino e le prossime offerte",
    body:
      "Ciao {name},\n\n" +
      "Grazie per l'interesse! In allegato trovi il nostro listino aggiornato con le offerte del momento. Se preferisci un preventivo su misura, dimmi solo di cosa hai bisogno e te lo preparo entro 24 ore.\n\n" +
      "Cordiali saluti,\nIl team GestAgePro",
  },
];

const applyPlaceholders = (str, name) => (str || "").replaceAll("{name}", name || "");

// 🆕 Feb 2026 — Lista icone selezionabili per i template risposte rapide.
// L'utente sceglie una tra queste emoji con un click; il valore è salvato
// nel campo `icon` del template e mostrato accanto all'etichetta nel bottone.
const TEMPLATE_ICONS = [
  "📞", "📱", "📧", "✉️", "💬", "📅", "⏰", "🕐",
  "💼", "🧾", "💰", "🎯", "📋", "📝", "📎", "📄",
  "✅", "❌", "⚠️", "ℹ️", "❓", "❗", "⭐", "🌟",
  "👋", "🤝", "👍", "🙏", "😊", "🎉", "🚀", "🔔",
  "🏢", "🏠", "🚗", "🛡️", "🔒", "🔑", "🎁", "🏥",
];

export default function SuperAdminContacts() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | unread | in_progress | closed
  const [detail, setDetail] = useState(null); // active request
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  // 🆕 Feb 2026 — Impostazioni: email di notifica configurabile
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyEmailInitial, setNotifyEmailInitial] = useState("");
  const [notifyEmailSaving, setNotifyEmailSaving] = useState(false);
  // 🆕 Feb 2026 — Template risposte rapide (CRUD SuperAdmin)
  const [templates, setTemplates] = useState([]);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [tplEditing, setTplEditing] = useState(null); // {id?, label, subject, body}
  const [tplSaving, setTplSaving] = useState(false);

  const loadTemplates = async () => {
    try {
      const { data } = await api.get("/superadmin/contact-reply-templates");
      setTemplates(Array.isArray(data) ? data : []);
    } catch { setTemplates([]); }
    setTemplatesLoaded(true);
  };

  const saveTemplate = async () => {
    const t = tplEditing;
    if (!t) return;
    const payload = { label: (t.label || "").trim(), subject: (t.subject || "").trim(), body: t.body || "", icon: (t.icon || "").trim() || null };
    if (!payload.label || !payload.subject || !payload.body) {
      toast.error("Etichetta, oggetto e messaggio sono obbligatori");
      return;
    }
    setTplSaving(true);
    try {
      if (t.id && !t.id.startsWith("__default_")) {
        await api.put(`/superadmin/contact-reply-templates/${t.id}`, payload);
        toast.success("Template aggiornato");
      } else {
        await api.post("/superadmin/contact-reply-templates", payload);
        toast.success("Template creato");
      }
      setTplEditing(null);
      await loadTemplates();
    } catch (e) {
      toast.error("Salvataggio non riuscito");
    } finally {
      setTplSaving(false);
    }
  };

  const removeTemplate = async (id) => {
    if (!id || id.startsWith("__default_")) return;
    if (!window.confirm("Eliminare definitivamente questo template?")) return;
    try {
      await api.delete(`/superadmin/contact-reply-templates/${id}`);
      toast.success("Template eliminato");
      await loadTemplates();
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  const loadSettings = async () => {
    try {
      const { data } = await api.get("/superadmin/contact-settings");
      const v = data?.contact_notify_email || "";
      setNotifyEmail(v);
      setNotifyEmailInitial(v);
    } catch { /* ignore */ }
  };

  const saveNotifyEmail = async () => {
    const val = (notifyEmail || "").trim();
    // Validazione lato client: se non vuoto deve essere email valida
    if (val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      toast.error("Indirizzo email non valido");
      return;
    }
    setNotifyEmailSaving(true);
    try {
      const { data } = await api.put("/superadmin/contact-settings", { contact_notify_email: val });
      const saved = data?.contact_notify_email || "";
      setNotifyEmail(saved);
      setNotifyEmailInitial(saved);
      toast.success(saved ? "Email di notifica salvata" : "Notifiche email disattivate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setNotifyEmailSaving(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/contact-requests");
      setItems(data?.items || []);
      setUnread(data?.unread || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento richieste");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadSettings(); loadTemplates(); }, []);

  const openDetail = async (item) => {
    setDetail(item);
    setNotes(item.notes || "");
    // Se non letta, marca come letta subito
    if (!item.is_read) {
      try {
        await api.patch(`/superadmin/contact-requests/${item.id}`, { is_read: true });
        setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, is_read: true } : x));
        setUnread((u) => Math.max(0, u - 1));
      } catch { /* ignore */ }
    }
  };

  const updateItem = async (id, patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/superadmin/contact-requests/${id}`, patch);
      setItems((prev) => prev.map((x) => x.id === id ? data : x));
      if (detail?.id === id) setDetail(data);
      toast.success("Aggiornato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento");
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Eliminare definitivamente questa richiesta?")) return;
    try {
      await api.delete(`/superadmin/contact-requests/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      setDetail(null);
      toast.success("Richiesta eliminata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    }
  };

  const filtered = items.filter((x) => {
    if (filter === "unread") return !x.is_read;
    if (filter === "in_progress") return x.status === "in_progress";
    if (filter === "closed") return x.status === "closed";
    return true;
  });

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid="superadmin-contacts-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold text-slate-800 flex items-center gap-2">
            <MessageCircle className="h-6 w-6 text-cyan-600" />
            Richieste di contatto
            {unread > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-rose-500 text-white text-xs font-bold" data-testid="contacts-unread-count">
                {unread} non lett{unread === 1 ? "a" : "e"}
              </span>
            )}
          </h1>
          <p className="text-sm text-slate-500 mt-1">Messaggi ricevuti dal form &quot;Contattaci&quot; nella landing pubblica.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 text-xs" data-testid="contacts-filter">
            {[
              { v: "all", l: "Tutte" },
              { v: "unread", l: "Non lette" },
              { v: "in_progress", l: "In lavorazione" },
              { v: "closed", l: "Chiuse" },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setFilter(opt.v)}
                data-testid={`filter-${opt.v}`}
                className={`px-3 py-1.5 rounded ${filter === opt.v ? "bg-cyan-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              >
                {opt.l}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="contacts-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Aggiorna
          </Button>
        </div>
      </div>

      {/* 🆕 Impostazioni notifiche */}
      <Card className="mb-4" data-testid="notify-email-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm text-slate-700 font-medium flex items-center gap-2">
            <BellRing className="h-4 w-4 text-cyan-600" />
            Email per ricevere i contatti
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-col sm:flex-row sm:items-end gap-2">
            <div className="flex-1">
              <Label htmlFor="notify-email-input" className="text-[10px] uppercase tracking-wider text-slate-500">
                Indirizzo di notifica
              </Label>
              <Input
                id="notify-email-input"
                data-testid="notify-email-input"
                type="email"
                autoComplete="email"
                placeholder="es. staff@tuaazienda.it (lascia vuoto per disattivare)"
                value={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.value)}
                className="mt-1"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Ogni nuova richiesta dal form &quot;Contattaci&quot; sarà inviata a questo indirizzo. Se il campo è vuoto, la richiesta viene comunque salvata ma non viene inviata alcuna notifica email.
              </p>
            </div>
            <Button
              onClick={saveNotifyEmail}
              disabled={notifyEmailSaving || notifyEmail === notifyEmailInitial}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="notify-email-save"
            >
              {notifyEmailSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              Salva
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm text-slate-600 font-medium">{filtered.length} messaggi</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-slate-400 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Caricamento...
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-400 italic">Nessuna richiesta {filter !== "all" && `(filtro: ${filter})`}.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs">
                <tr>
                  <th className="text-left px-4 py-2 w-8"></th>
                  <th className="text-left px-4 py-2">Contatto</th>
                  <th className="text-left px-4 py-2">Messaggio</th>
                  <th className="text-left px-4 py-2">Stato</th>
                  <th className="text-right px-4 py-2">Ricevuto</th>
                  <th className="text-right px-4 py-2">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => {
                  const s = STATUS_META[it.status] || STATUS_META.new;
                  const SIcon = s.icon;
                  const dt = it.created_at ? new Date(it.created_at) : null;
                  return (
                    <tr
                      key={it.id}
                      data-testid={`contact-row-${it.id}`}
                      className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${!it.is_read ? "bg-cyan-50/50 font-medium" : ""}`}
                      onClick={() => openDetail(it)}
                    >
                      <td className="px-4 py-2.5">
                        {!it.is_read ? (
                          <span className="inline-block h-2 w-2 rounded-full bg-cyan-500" title="Non letta" />
                        ) : (
                          <CheckCircle2 className="h-3.5 w-3.5 text-slate-300" />
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="text-slate-800 font-medium">{it.name}</div>
                        <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                          <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{it.email}</span>
                          {it.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{it.phone}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-slate-600 text-[13px] max-w-md truncate" title={it.message}>
                        {it.message}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-semibold uppercase tracking-wide ${s.cls}`}>
                          <SIcon className="h-3 w-3" />{s.label}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right text-xs text-slate-500 tabular-nums">
                        {dt ? dt.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); removeItem(it.id); }}
                          className="text-rose-600 hover:bg-rose-50 rounded p-1"
                          data-testid={`delete-contact-${it.id}`}
                          title="Elimina"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Dettaglio */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-2xl" data-testid="contact-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-cyan-600" />
              Richiesta di contatto
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Nome</Label>
                  <div className="text-slate-800 font-medium">{detail.name}</div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Ricevuto</Label>
                  <div className="text-slate-700 tabular-nums">
                    {detail.created_at ? new Date(detail.created_at).toLocaleString("it-IT") : "—"}
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Email</Label>
                  <a href={`mailto:${detail.email}`} className="text-cyan-700 hover:underline block">{detail.email}</a>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Telefono</Label>
                  <div className="text-slate-700">
                    {detail.phone ? <a href={`tel:${detail.phone}`} className="hover:underline">{detail.phone}</a> : "—"}
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Messaggio</Label>
                <div className="mt-1 bg-slate-50 border border-slate-200 rounded-md p-3 text-sm text-slate-800 whitespace-pre-wrap max-h-56 overflow-y-auto">
                  {detail.message}
                </div>
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Stato</Label>
                <div className="mt-1.5 inline-flex rounded-md border border-slate-200 bg-white p-0.5">
                  {Object.entries(STATUS_META).map(([v, m]) => (
                    <button
                      key={v}
                      data-testid={`status-btn-${v}`}
                      onClick={() => updateItem(detail.id, { status: v })}
                      disabled={saving}
                      className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${detail.status === v ? "bg-cyan-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Risposte rapide</Label>
                  <button
                    type="button"
                    data-testid="manage-templates-btn"
                    onClick={() => setManageOpen(true)}
                    className="text-[11px] text-cyan-700 hover:text-cyan-900 hover:underline inline-flex items-center gap-1"
                  >
                    <Settings2 className="h-3 w-3" /> Gestisci
                  </button>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="quick-reply-templates">
                  {(() => {
                    const list = templatesLoaded && templates.length ? templates : DEFAULT_REPLY_TEMPLATES;
                    if (templatesLoaded && !templates.length) {
                      return (
                        <p className="text-xs text-slate-500 italic">
                          Nessun template salvato — clicca <span className="font-semibold">Gestisci</span> per crearne uno o usa i due suggerimenti qui sotto.
                        </p>
                      );
                    }
                    return list.map((tpl) => {
                      const body = applyPlaceholders(tpl.body, detail.name || "");
                      const subject = applyPlaceholders(tpl.subject, detail.name || "");
                      const receivedAt = detail.created_at ? new Date(detail.created_at).toLocaleString("it-IT") : "";
                      const quoted = String(detail.message || "").split("\n").map((l) => `> ${l}`).join("\n");
                      const fullBody = `${body}\n\n---\nIl ${receivedAt} hai scritto:\n${quoted}`;
                      const mailtoHref = `mailto:${encodeURIComponent(detail.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(fullBody)}`;
                      const phoneClean = (detail.phone || "").replace(/[^0-9+]/g, "").replace(/^\+/, "");
                      const waHref = phoneClean ? `https://wa.me/${phoneClean}?text=${encodeURIComponent(body)}` : null;
                      return (
                        <div key={tpl.id} className="inline-flex rounded-md border border-slate-300 overflow-hidden">
                          <a
                            href={mailtoHref}
                            data-testid={`quick-reply-email-${tpl.id}`}
                            onClick={() => { if (detail.status === "new") updateItem(detail.id, { status: "in_progress" }); }}
                            className="px-3 py-1.5 text-xs font-medium bg-white hover:bg-cyan-50 text-slate-700 hover:text-cyan-700 transition-colors flex items-center gap-1.5"
                            title={`Rispondi via email: ${subject}`}
                          >
                            {tpl.icon && <span className="text-base leading-none">{tpl.icon}</span>}
                            <span>{tpl.label}</span>
                          </a>
                          {waHref && (
                            <a
                              href={waHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`quick-reply-whatsapp-${tpl.id}`}
                              onClick={() => { if (detail.status === "new") updateItem(detail.id, { status: "in_progress" }); }}
                              className="px-2 py-1.5 text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-l border-slate-300 transition-colors"
                              title="Invia via WhatsApp"
                            >
                              📱
                            </a>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
                <p className="text-[10px] text-slate-500 mt-1.5">Un click apre l&apos;app email (o WhatsApp se disponibile) con la risposta già scritta. La richiesta passa automaticamente a &quot;In lavorazione&quot;.</p>
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Note interne</Label>
                <textarea
                  data-testid="contact-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onBlur={() => { if (notes !== (detail.notes || "")) updateItem(detail.id, { notes }); }}
                  rows={3}
                  maxLength={2000}
                  className="mt-1 w-full rounded-md border border-slate-200 bg-white text-sm px-3 py-2"
                  placeholder="Aggiungi note (salva automaticamente uscendo dal campo)..."
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            {detail && (
              <Button
                onClick={() => {
                  const subject = `Re: la tua richiesta a GestAgePro`;
                  const receivedAt = detail.created_at ? new Date(detail.created_at).toLocaleString("it-IT") : "";
                  const quoted = String(detail.message || "").split("\n").map((l) => `> ${l}`).join("\n");
                  const body =
                    `Ciao ${detail.name},\n\n` +
                    `Grazie per averci contattato. Rispondo alla tua richiesta qui sotto.\n\n` +
                    `[Scrivi qui la tua risposta]\n\n` +
                    `---\n` +
                    `Il ${receivedAt} hai scritto:\n${quoted}\n\n` +
                    `Cordiali saluti,\nIl team GestAgePro`;
                  const href = `mailto:${encodeURIComponent(detail.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                  window.location.href = href;
                  // Marca automaticamente come "in lavorazione" se ancora "new"
                  if (detail.status === "new") updateItem(detail.id, { status: "in_progress" });
                }}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="detail-reply-email"
              >
                <Reply className="h-4 w-4 mr-1" /> Rispondi via email
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => removeItem(detail.id)}
              className="text-rose-700 border-rose-300 hover:bg-rose-50"
              data-testid="detail-delete"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Elimina
            </Button>
            <Button variant="outline" onClick={() => setDetail(null)} data-testid="detail-close">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Feb 2026 — Dialog Gestione Template Risposte Rapide */}
      <Dialog open={manageOpen} onOpenChange={(o) => { if (!o) { setManageOpen(false); setTplEditing(null); } }}>
        <DialogContent className="max-w-3xl" data-testid="manage-templates-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-cyan-600" />
              Gestione risposte rapide
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {!tplEditing && (
              <>
                <div className="flex justify-between items-center">
                  <p className="text-xs text-slate-600">
                    Crea, modifica ed elimina le risposte che appariranno nel dettaglio contatto.
                    Usa <code className="px-1 py-0.5 bg-slate-100 rounded text-[11px]">{`{name}`}</code> nell&apos;oggetto o nel corpo per inserire automaticamente il nome del contatto.
                  </p>
                  <Button
                    data-testid="tpl-new-btn"
                    onClick={() => setTplEditing({ label: "", subject: "", body: "Ciao {name},\n\n" })}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Nuovo template
                  </Button>
                </div>
                {templates.length === 0 ? (
                  <p className="text-sm text-slate-500 italic py-6 text-center bg-slate-50 rounded-md border border-slate-200">
                    Nessun template salvato. Clicca «Nuovo template» per crearne uno.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {templates.map((t) => (
                      <div key={t.id} data-testid={`tpl-row-${t.id}`} className="flex items-start justify-between gap-3 border border-slate-200 rounded-md p-3 bg-white">
                        <div className="flex-1 min-w-0 flex items-start gap-2">
                          {t.icon && <span className="text-xl leading-none mt-0.5">{t.icon}</span>}
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-800 text-sm">{t.label}</div>
                            <div className="text-xs text-slate-500 mt-0.5"><strong>Oggetto:</strong> {t.subject}</div>
                            <div className="text-[11px] text-slate-500 mt-1 whitespace-pre-wrap line-clamp-3">{t.body}</div>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <button
                            data-testid={`tpl-edit-${t.id}`}
                            onClick={() => setTplEditing({ ...t })}
                            className="p-1.5 rounded hover:bg-cyan-50 text-cyan-700"
                            title="Modifica"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            data-testid={`tpl-delete-${t.id}`}
                            onClick={() => removeTemplate(t.id)}
                            className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                            title="Elimina"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            {tplEditing && (
              <div className="space-y-3">
                <div>
                  <Label>Icona</Label>
                  <div className="mt-1 flex flex-wrap gap-1.5 p-2 border border-slate-200 rounded-md bg-slate-50 max-h-40 overflow-y-auto" data-testid="tpl-icon-picker">
                    <button
                      type="button"
                      data-testid="tpl-icon-none"
                      onClick={() => setTplEditing({ ...tplEditing, icon: "" })}
                      className={`w-9 h-9 rounded-md border text-xs font-semibold transition ${!tplEditing.icon ? "bg-cyan-600 text-white border-cyan-700" : "bg-white text-slate-500 border-slate-300 hover:bg-slate-100"}`}
                      title="Nessuna icona"
                    >
                      ⌀
                    </button>
                    {TEMPLATE_ICONS.map((ic) => (
                      <button
                        key={ic}
                        type="button"
                        data-testid={`tpl-icon-${ic}`}
                        onClick={() => setTplEditing({ ...tplEditing, icon: ic })}
                        className={`w-9 h-9 rounded-md border text-lg transition ${tplEditing.icon === ic ? "bg-cyan-600 border-cyan-700 ring-2 ring-cyan-300" : "bg-white border-slate-300 hover:bg-slate-100"}`}
                        title={`Usa ${ic}`}
                      >
                        {ic}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">Facoltativa. L&apos;icona apparirà accanto all&apos;etichetta del pulsante.</p>
                </div>
                <div>
                  <Label>Etichetta pulsante</Label>
                  <Input
                    data-testid="tpl-input-label"
                    value={tplEditing.label || ""}
                    onChange={(e) => setTplEditing({ ...tplEditing, label: e.target.value })}
                    placeholder="Es. Ti chiamo io"
                    maxLength={80}
                  />
                </div>
                <div>
                  <Label>Oggetto email</Label>
                  <Input
                    data-testid="tpl-input-subject"
                    value={tplEditing.subject || ""}
                    onChange={(e) => setTplEditing({ ...tplEditing, subject: e.target.value })}
                    placeholder="Es. Ti ricontatto io a breve"
                    maxLength={200}
                  />
                </div>
                <div>
                  <Label>Messaggio</Label>
                  <textarea
                    data-testid="tpl-input-body"
                    value={tplEditing.body || ""}
                    onChange={(e) => setTplEditing({ ...tplEditing, body: e.target.value })}
                    rows={10}
                    maxLength={5000}
                    className="w-full rounded-md border border-slate-200 bg-white text-sm px-3 py-2 font-mono"
                    placeholder={"Ciao {name},\n\nScrivi qui il tuo messaggio..."}
                  />
                  <p className="text-[10px] text-slate-500 mt-1">Usa <code className="px-1 bg-slate-100 rounded">{`{name}`}</code> per inserire automaticamente il nome del contatto.</p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            {tplEditing ? (
              <>
                <Button variant="outline" onClick={() => setTplEditing(null)} disabled={tplSaving}>Annulla</Button>
                <Button
                  data-testid="tpl-save-btn"
                  onClick={saveTemplate}
                  disabled={tplSaving}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white"
                >
                  {tplSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                  Salva
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setManageOpen(false)} data-testid="manage-close">Chiudi</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
